import React, { useState } from "react";
import { useTranslation, useLocale } from "./contexts/LocaleContext";
import { authenticatedFetch } from "./config/api";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import {
  Send,
  Cloud,
  Mail,
  AlertCircle,
  CheckCircle2,
  Code2,
  Loader2,
  BookText,
  Copy,
} from "lucide-react";
import JsonSpecificationModal from "./components/JsonSpecificationModal";

export default function TransferInvoices() {
  const t = useTranslation();
  const { locale } = useLocale();

  // Manual request states (Steps 1 & 2)
  const [message, setMessage] = useState(
    (t as any).transferInvoices.prefilledText
  );
  const [link, setLink] = useState("");
  const [isManualSubmitting, setIsManualSubmitting] = useState(false);
  const [manualSuccess, setManualSuccess] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);

  // Automatic request states (Step 3)
  const [jsonData, setJsonData] = useState("");
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);
  const [autoSuccessCount, setAutoSuccessCount] = useState<number | null>(null);
  const [autoError, setAutoError] = useState<string | null>(null);

  // Specification modal
  const [isSpecModalOpen, setIsSpecModalOpen] = useState(false);
  const [copiedLlm, setCopiedLlm] = useState(false);

  const handleCopyLlm = () => {
    const prompt = `Act as a data migration expert. Your task is to convert invoice data from various formats (text, CSV, images, or other JSON structures) into the specific JSON format required by the Invoitio invoice generator.

### TARGET JSON STRUCTURE:
{
  "invoices": [
    {
      "draft": {
        "invoiceNo": "string",
        "issueDate": "string (YYYY-MM-DD)",
        "dueDate": "string (YYYY-MM-DD)",
        "currency": "string (e.g., 'CZK')",
        "buyerName": "string",
        "buyerAddress": "string",
        "buyerId": "string (IČO)",
        "buyerTaxId": "string (optional, DIČ)",
        "items": [
          {
            "id": "string (unique id)",
            "description": "string",
            "quantity": number,
            "amount": number,
            "total": number
          }
        ],
        "total": number,
        "note": "string (optional)",
        "vatEnabled": boolean (optional),
        "vatRate": number (optional),
        "sellerInfo": {
          "name": "string",
          "address": "string",
          "identificationNumber": "string",
          "country": "string"
        }
      },
      "designSettings": {
        "template": "Стандартный",
        "fontFamily": "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, \"Helvetica Neue\", Arial",
        "accentColor": "#2563eb",
        "textColor": "#000000",
        "backgroundColor": "#ffffff"
      },
      "language": "cs"
    }
  ]
}

### INSTRUCTIONS:
1. Ensure all dates are in YYYY-MM-DD format. Use 'issueDate' for the date of the invoice.
2. All numeric values must be numbers. Use 'amount' for unit price.
3. 'buyerId' is the company registration number (IČO).
4. ALWAYS use the following fixed 'designSettings': {"template": "Стандартный", "fontFamily": "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, \"Helvetica Neue\", Arial", "accentColor": "#2563eb", "textColor": "#000000", "backgroundColor": "#ffffff"}.
5. Output ONLY the final valid JSON object. No explanations.

Please convert the following data:
[PASTE YOUR DATA HERE]`;

    navigator.clipboard.writeText(prompt);
    setCopiedLlm(true);
    setTimeout(() => setCopiedLlm(false), 3000);
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsManualSubmitting(true);
    setManualError(null);

    try {
      const response = await authenticatedFetch("/api/transfer-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: DOMPurify.sanitize(message),
          link: DOMPurify.sanitize(link),
          language: locale,
        }),
      });

      if (response.ok) {
        setManualSuccess(true);
      } else {
        const text = await response.text();
        let errorMsg = "Failed to send request";
        try {
          const data = JSON.parse(text);
          errorMsg = data.error || errorMsg;
        } catch (e) {
          errorMsg = `Server error (${response.status}): ${response.statusText}`;
        }
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      setManualError(err.message);
    } finally {
      setIsManualSubmitting(false);
    }
  };

  const handleAutoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsAutoSubmitting(true);
    setAutoError(null);
    setAutoSuccessCount(null);

    let parsedJson = null;
    try {
      parsedJson = JSON.parse(jsonData);
    } catch (err) {
      setAutoError("Invalid JSON format.");
      setIsAutoSubmitting(false);
      return;
    }

    // Sanitize JSON values (deeply)
    const sanitizeValue = (val: any): any => {
      if (typeof val === "string") return DOMPurify.sanitize(val);
      if (Array.isArray(val)) return val.map(sanitizeValue);
      if (val !== null && typeof val === "object") {
        const result: any = {};
        for (const k in val) {
          result[k] = sanitizeValue(val[k]);
        }
        return result;
      }
      return val;
    };

    const sanitizedJson = sanitizeValue(parsedJson);

    try {
      const response = await authenticatedFetch("/api/transfer-auto-create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jsonData: sanitizedJson,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setAutoSuccessCount(data.count);
      } else {
        const text = await response.text();
        let errorMsg = "Failed to create invoices";
        try {
          const data = JSON.parse(text);
          errorMsg = data.error || errorMsg;
        } catch (e) {
          // If not JSON, use the status text or a snippet of the response
          errorMsg = `Server error (${response.status}): ${response.statusText}`;
          console.error("Non-JSON error response:", text);
        }
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      setAutoError(err.message);
    } finally {
      setIsAutoSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="space-y-3">
        <h1 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white tracking-tight uppercase italic">
          <span className="bg-indigo-600 text-white px-4 py-1 -rotate-1 inline-block shadow-lg">
            {(t as any).transferInvoices.title}
          </span>
        </h1>
        <p
          className="text-base md:text-lg text-slate-600 dark:text-slate-400 font-medium leading-relaxed max-w-2xl"
          dangerouslySetInnerHTML={{
            __html: (t as any).transferInvoices.description.replace(
              /\*\*(.*?)\*\*/g,
              "<strong>$1</strong>"
            ),
          }}
        />
      </div>

      <div className="relative">
        <div className="flex flex-col gap-6">
          {/* Manual Section - Narrower and Centered */}
          <div className="flex justify-center">
            <div
              className={`w-full max-w-2xl bg-white dark:bg-slate-800/50 rounded-[2.5rem] border border-slate-200 dark:border-slate-700 p-5 md:p-6 shadow-sm transition-all`}
            >
              {manualSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="h-full flex flex-col items-center justify-center text-center space-y-6 py-12"
                >
                  <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="w-12 h-12" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                    {(t as any).transferInvoices.success}
                  </h2>
                </motion.div>
              ) : (
                <form onSubmit={handleManualSubmit} className="space-y-3">
                  <div className="space-y-2.5">
                    <h2 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-3">
                      <span className="w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[9px] font-black">
                        1
                      </span>
                      {(t as any).transferInvoices.setupRequired}
                    </h2>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="w-full h-16 px-4 py-2 bg-slate-50 dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none resize-none font-medium text-sm"
                    />
                  </div>

                  <div className="space-y-2.5">
                    <h2 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-3">
                      <span className="w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[9px] font-black">
                        2
                      </span>
                      {(t as any).transferInvoices.storageLink}
                    </h2>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Cloud className="w-4 h-4 text-slate-400" />
                      </div>
                      <input
                        type="text"
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                        placeholder={
                          (t as any).transferInvoices.storageLinkPlaceholder
                        }
                        className="w-full pl-11 pr-5 py-2 bg-slate-50 dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium text-sm"
                      />
                    </div>
                  </div>

                  {manualError && (
                    <div className="p-2.5 bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/30 rounded-xl flex items-center gap-2 text-rose-600 dark:text-rose-400 text-[10px] font-bold">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {manualError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isManualSubmitting || !link.trim()}
                    className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-black shadow-lg shadow-indigo-500/25 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 text-xs"
                  >
                    {isManualSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                    {(t as any).transferInvoices.manualSubmitButton}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Auto Section - Wider */}
          <div
            className={`w-full bg-white dark:bg-slate-800/50 rounded-[2.5rem] border border-slate-200 dark:border-slate-700 p-6 md:p-10 shadow-sm transition-all`}
          >
            {autoSuccessCount !== null ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="h-full flex flex-col items-center justify-center text-center space-y-6 py-12"
              >
                <div className="w-24 h-24 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="w-14 h-14" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                    {(t as any).transferInvoices.autoSuccess.replace(
                      "{count}",
                      autoSuccessCount
                    )}
                  </h2>
                </div>
                <button
                  onClick={() => setAutoSuccessCount(null)}
                  className="text-base font-bold text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  Create more
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={handleAutoSubmit}
                className="space-y-6 h-full flex flex-col"
              >
                <div className="space-y-3 bg-indigo-50/50 dark:bg-indigo-900/10 p-5 rounded-[2rem] border border-indigo-100 dark:border-indigo-800/50">
                  <h3 className="text-base font-bold text-slate-900 dark:text-white">
                    {(t as any).transferInvoices.aiTransferTitle}
                  </h3>
                  <p className="text-xs text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
                    {(t as any).transferInvoices.aiTransferDescription
                      .split("{button}")
                      .map((part: string, i: number, arr: any[]) => (
                        <React.Fragment key={i}>
                          {part}
                          {i < arr.length - 1 && (
                            <button
                              type="button"
                              onClick={handleCopyLlm}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 mx-1 rounded-lg text-xs font-bold transition-all border align-middle -translate-y-[1px] ${
                                copiedLlm
                                  ? "bg-emerald-500 text-white border-emerald-500 shadow-sm"
                                  : "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-indigo-100 dark:border-indigo-800 hover:border-indigo-500"
                              }`}
                            >
                              {copiedLlm ? (
                                <CheckCircle2 size={12} />
                              ) : (
                                <Copy size={12} />
                              )}
                              {(t as any).transferInvoices.copyLlmButton}
                            </button>
                          )}
                        </React.Fragment>
                      ))}
                  </p>
                </div>

                <div className="space-y-4 flex-1">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <h2 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-4">
                      <span className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-base font-black">
                        3
                      </span>
                      {(t as any).transferInvoices.jsonDataLabel}
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setIsSpecModalOpen(true)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl text-[10px] font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors border border-slate-200 dark:border-slate-700"
                      >
                        <BookText size={12} />
                        {(t as any).transferInvoices.specificationButton}
                      </button>
                      <button
                        type="button"
                        onClick={handleCopyLlm}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all border ${
                          copiedLlm
                            ? "bg-emerald-500 text-white border-emerald-500 shadow-md"
                            : "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-indigo-100 dark:border-indigo-800 hover:border-indigo-500"
                        }`}
                      >
                        {copiedLlm ? (
                          <CheckCircle2 size={12} />
                        ) : (
                          <Copy size={12} />
                        )}
                        {(t as any).transferInvoices.copyLlmButton}
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 font-medium px-1">
                    {(t as any).transferInvoices.jsonDataHelp}
                  </p>
                  <div className="relative flex-1">
                    <div className="absolute top-5 left-5 pointer-events-none">
                      <Code2 className="w-5 h-5 text-slate-400" />
                    </div>
                    <textarea
                      value={jsonData}
                      onChange={(e) => setJsonData(e.target.value)}
                      placeholder={
                        (t as any).transferInvoices.jsonDataPlaceholder
                      }
                      className="w-full h-[350px] pl-14 pr-6 py-5 bg-slate-50 dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-[2rem] text-slate-900 dark:text-white focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none resize-none font-mono text-xs leading-relaxed shadow-inner"
                    />
                  </div>
                </div>

                {autoError && (
                  <div className="p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/30 rounded-2xl flex items-center gap-3 text-rose-600 dark:text-rose-400 font-bold">
                    <AlertCircle className="w-5 h-5" />
                    {autoError}
                  </div>
                )}

                <div className="flex justify-center pt-4">
                  <button
                    type="submit"
                    disabled={isAutoSubmitting || !jsonData.trim()}
                    className="w-full max-w-xl py-5 rounded-[2rem] bg-indigo-600 hover:bg-indigo-700 text-white font-black text-lg shadow-xl shadow-indigo-500/25 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3"
                  >
                    {isAutoSubmitting ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <Code2 className="w-6 h-6" />
                    )}
                    {(t as any).transferInvoices.autoSubmitButton}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className="bg-slate-50 dark:bg-slate-900/50 rounded-[2rem] p-6 border border-slate-100 dark:border-slate-800 flex items-start gap-4">
        <div className="p-3 bg-white dark:bg-slate-800 rounded-2xl shadow-sm text-indigo-600 dark:text-indigo-400">
          <Mail className="w-6 h-6" />
        </div>
        <div className="space-y-1">
          <p className="font-bold text-slate-900 dark:text-white">
            {(t as any).transferInvoices.emailNote.split("{email}")[0]}
            <a
              href="mailto:alice@invoitio.com"
              className="text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              alice@invoitio.com
            </a>
            {(t as any).transferInvoices.emailNote.split("{email}")[1]}
          </p>
        </div>
      </div>

      <JsonSpecificationModal
        isOpen={isSpecModalOpen}
        onClose={() => setIsSpecModalOpen(false)}
      />
    </div>
  );
}
